import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  PlusCircle, Edit2, Trash2, Save, X, Loader2,
  ChevronUp, ChevronDown, Link2, Layers, ExternalLink,
} from 'lucide-react';
import clsx from 'clsx';

const API = process.env.REACT_APP_API_URL || '';
const MARKETING_URL = process.env.REACT_APP_MARKETING_URL || 'https://rostery.com.au';

/* ── Hub Preview ───────────────────────────────────────────────────── */
function HubPreview({ integrations }) {
  const active = integrations.filter(i => i.is_active);
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
      <h2 className="text-sm font-semibold text-slate-700 mb-4">Hub Preview</h2>
      <div className="flex flex-col items-center gap-6">
        {/* Center node */}
        <div className="relative flex items-center justify-center w-20 h-20 rounded-full shadow-lg"
          style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}>
          <span className="text-white font-bold text-sm">ShiftCore</span>
        </div>
        {/* Orbiting nodes as flex wrap */}
        {active.length > 0 ? (
          <div className="flex flex-wrap justify-center gap-2 max-w-lg">
            {active.map(int => (
              <div key={int.id}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold shadow-sm border border-slate-100"
                style={{
                  backgroundColor: int.bg_color || '#6366f1',
                  color: int.text_color || '#ffffff',
                }}>
                <span>{int.abbreviation || int.name?.slice(0, 2).toUpperCase()}</span>
                <span className="opacity-80">{int.name}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-400">No active integrations</p>
        )}
      </div>
    </div>
  );
}

/* ── Modal ─────────────────────────────────────────────────────────── */
function IntegrationModal({ integration, onClose, onSaved }) {
  const isEdit = Boolean(integration?.id);
  const token = localStorage.getItem('token');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: integration?.name || '',
    abbreviation: integration?.abbreviation || '',
    category: integration?.category || '',
    bg_color: integration?.bg_color || '#6366f1',
    text_color: integration?.text_color || '#ffffff',
    angle: integration?.angle ?? 0,
    website_url: integration?.website_url || '',
    sort_order: integration?.sort_order ?? 0,
    is_active: integration?.is_active !== false,
    scheduled_at: integration?.scheduled_at || null,
  });

  function set(field) {
    return e => setForm(f => ({ ...f, [field]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (!form.abbreviation.trim()) { toast.error('Abbreviation is required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        angle: parseInt(form.angle) || 0,
        sort_order: parseInt(form.sort_order) || 0,
        scheduled_at: form.scheduled_at || null,
      };
      const url = isEdit
        ? `${API}/api/marketing/integrations/${integration.id}`
        : `${API}/api/marketing/integrations`;
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error((await res.json()).message || 'Save failed');
      toast.success(isEdit ? 'Integration updated' : 'Integration created');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-slate-100 shrink-0">
          <h2 className="text-lg font-bold text-slate-900">
            {isEdit ? 'Edit Integration' : 'Add Integration'}
          </h2>
          <button onClick={onClose} className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
            <X size={16} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Name *</label>
              <input type="text" value={form.name} onChange={set('name')} required
                className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/30"
                placeholder="e.g. Xero" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Abbreviation * (2-3 chars)</label>
              <input type="text" value={form.abbreviation} onChange={set('abbreviation')} required maxLength={3}
                className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/30"
                placeholder="e.g. XE" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Category</label>
              <input type="text" value={form.category} onChange={set('category')}
                className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/30"
                placeholder="e.g. Accounting" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Background Color</label>
              <div className="flex items-center gap-2">
                <input type="color" value={form.bg_color} onChange={set('bg_color')}
                  className="w-10 h-10 rounded-lg border border-slate-200 cursor-pointer p-0.5" />
                <input type="text" value={form.bg_color} onChange={set('bg_color')}
                  className="flex-1 px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/30 font-mono" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Text Color</label>
              <div className="flex items-center gap-2">
                <input type="color" value={form.text_color} onChange={set('text_color')}
                  className="w-10 h-10 rounded-lg border border-slate-200 cursor-pointer p-0.5" />
                <input type="text" value={form.text_color} onChange={set('text_color')}
                  className="flex-1 px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/30 font-mono" />
              </div>
            </div>
            {/* Node preview */}
            <div className="col-span-2 flex items-center gap-3">
              <span className="text-sm text-slate-500">Preview:</span>
              <div className="flex items-center justify-center w-10 h-10 rounded-full text-xs font-bold shadow"
                style={{ backgroundColor: form.bg_color, color: form.text_color }}>
                {form.abbreviation || '??'}
              </div>
              <span className="text-sm font-medium" style={{ color: form.bg_color }}>{form.name || 'Integration Name'}</span>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Angle (0-360°)</label>
              <input type="number" min={0} max={360} value={form.angle} onChange={set('angle')}
                className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/30" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Sort Order</label>
              <input type="number" min={0} value={form.sort_order} onChange={set('sort_order')}
                className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/30" />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Website URL (optional)</label>
              <input type="url" value={form.website_url} onChange={set('website_url')}
                className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/30"
                placeholder="https://xero.com" />
            </div>
            <div className="flex items-center gap-3">
              <input type="checkbox" id="int_active" checked={form.is_active}
                onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))}
                className="w-4 h-4 accent-primary-600 rounded" />
              <label htmlFor="int_active" className="text-sm font-medium text-slate-700">Active</label>
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Schedule publish date (optional)</label>
              <p className="text-xs text-slate-400 mb-2">Leave empty to publish immediately when active. Set a future date to auto-publish at that time.</p>
              <input
                type="datetime-local"
                value={form.scheduled_at ? form.scheduled_at.slice(0, 16) : ''}
                onChange={e => setForm(f => ({ ...f, scheduled_at: e.target.value || null }))}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400/40 focus:border-teal-400"
              />
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2.5 text-sm font-semibold text-slate-600 bg-slate-100 rounded-xl hover:bg-slate-200 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-primary-600 rounded-xl hover:bg-primary-700 transition-colors disabled:opacity-60">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? 'Saving...' : 'Save Integration'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ── Main Page ─────────────────────────────────────────────────────── */
export default function IntegrationsEditor() {
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const token = localStorage.getItem('token');

  async function fetchIntegrations() {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/marketing/integrations/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setIntegrations(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Failed to load integrations');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchIntegrations(); }, []);

  async function deleteIntegration(id, name) {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try {
      const res = await fetch(`${API}/api/marketing/integrations/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      toast.success('Integration deleted');
      fetchIntegrations();
    } catch {
      toast.error('Failed to delete');
    }
  }

  async function toggleActive(int) {
    try {
      await fetch(`${API}/api/marketing/integrations/${int.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...int, is_active: !int.is_active }),
      });
      fetchIntegrations();
    } catch {
      toast.error('Failed to update');
    }
  }

  async function reorder(index, direction) {
    const arr = [...integrations];
    const swap = direction === 'up' ? index - 1 : index + 1;
    if (swap < 0 || swap >= arr.length) return;
    [arr[index], arr[swap]] = [arr[swap], arr[index]];
    setIntegrations(arr);
    try {
      await Promise.all(
        arr.map((it, i) =>
          fetch(`${API}/api/marketing/integrations/${it.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ ...it, sort_order: i }),
          })
        )
      );
      fetchIntegrations();
    } catch {
      toast.error('Failed to reorder');
    }
  }

  return (
    <div className="p-6 space-y-6">
      {modal && (
        <IntegrationModal
          integration={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={fetchIntegrations}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Integrations</h1>
          <p className="text-slate-500 text-sm mt-0.5">
            {integrations.length} integration{integrations.length !== 1 ? 's' : ''} — shown in the hub diagram on the homepage
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a href={`${MARKETING_URL}/#integrations`} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-teal-600 border border-teal-200 rounded-xl hover:bg-teal-50 transition-colors">
            <ExternalLink size={14} />
            Preview on Site
          </a>
          <button
            onClick={() => setModal('new')}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-xl text-sm font-semibold hover:bg-primary-700 transition-colors shadow-sm">
            <PlusCircle size={16} />
            Add Integration
          </button>
        </div>
      </div>

      {/* Hub preview */}
      {!loading && <HubPreview integrations={integrations} />}

      {/* List */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : integrations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-400">
            <Layers size={40} className="mb-3 opacity-30" />
            <p className="font-medium">No integrations yet</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50">
                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-5 py-3">Integration</th>
                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3">Category</th>
                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3">Angle</th>
                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3">Status</th>
                <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wider px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {integrations.map((int, index) => {
                const isScheduled = int.scheduled_at && new Date(int.scheduled_at) > new Date();
                return (
                <tr key={int.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0 shadow-sm"
                        style={{ backgroundColor: int.bg_color || '#6366f1', color: int.text_color || '#fff' }}>
                        {int.abbreviation || int.name?.slice(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-900">{int.name}</p>
                        {int.website_url && (
                          <a href={int.website_url} target="_blank" rel="noreferrer"
                            className="text-xs text-primary-500 hover:underline flex items-center gap-0.5">
                            <Link2 size={10} /> {int.website_url.replace(/^https?:\/\//, '')}
                          </a>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3.5">
                    {int.category && (
                      <span className="text-xs font-medium text-slate-600 bg-slate-100 px-2.5 py-1 rounded-full">{int.category}</span>
                    )}
                  </td>
                  <td className="px-4 py-3.5">
                    <span className="text-sm text-slate-500 font-mono">{int.angle ?? 0}°</span>
                  </td>
                  <td className="px-4 py-3.5">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <button onClick={() => toggleActive(int)}
                        className={clsx(
                          'inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold transition-colors',
                          int.is_active
                            ? 'bg-emerald-500/15 text-emerald-600 hover:bg-emerald-500/25'
                            : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
                        )}>
                        {int.is_active ? 'Active' : 'Hidden'}
                      </button>
                      {isScheduled && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700 border border-amber-200">
                          Scheduled
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => reorder(index, 'up')} disabled={index === 0}
                        className="p-1.5 rounded-lg text-slate-300 hover:text-slate-600 hover:bg-slate-100 transition-colors disabled:opacity-30">
                        <ChevronUp size={14} />
                      </button>
                      <button onClick={() => reorder(index, 'down')} disabled={index === integrations.length - 1}
                        className="p-1.5 rounded-lg text-slate-300 hover:text-slate-600 hover:bg-slate-100 transition-colors disabled:opacity-30">
                        <ChevronDown size={14} />
                      </button>
                      <button onClick={() => setModal(int)}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-primary-600 hover:bg-primary-50 transition-colors">
                        <Edit2 size={14} />
                      </button>
                      <button onClick={() => deleteIntegration(int.id, int.name)}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
